import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { VehicleCard } from "@/components/VehicleCard";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { ShieldCheck, Link2, Search, Sparkles, ArrowRight, Home, HandCoins, BadgeCheck, Truck, Tag, Car } from "lucide-react";
import { useContent, LANDING_DEFAULTS } from "@/lib/content";
import { SiteFooter } from "@/components/SiteFooter";
import { PROMO_TYPES, promoBadge, promoMatchesVehicle, headlineFigure, termLine, scopeLabel } from "@/lib/promotions";
import { OfferCountdown } from "@/components/OfferCountdown";
import { BrandMark } from "@/components/BrandMark";

function SpecialsBanner() {
  const navigate = useNavigate();
  const [promos, setPromos] = useState([]);
  const [i, setI] = useState(0);
  useEffect(() => {
    api.get("/promotions/active").then(({ data }) => setPromos((data || []).filter((p) => p.scope === "manufacturer"))).catch(() => {});
  }, []);
  useEffect(() => {
    if (promos.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % promos.length), 5000);
    return () => clearInterval(t);
  }, [promos]);
  const p = promos.length ? promos[i % promos.length] : null;
  useEffect(() => { if (p) api.post(`/promotions/${p.id}/view`).catch(() => {}); }, [p && p.id]); // eslint-disable-line
  if (!p) return null;
  return (
    <button onClick={() => { api.post(`/promotions/${p.id}/click`).catch(() => {}); navigate("/specials"); }} data-testid="specials-banner"
      className={`flex w-full items-center justify-center gap-3 px-4 py-2.5 text-sm font-bold transition-colors ${PROMO_TYPES[p.promo_type]?.cls || "bg-rose-600 text-white"}`}>
      <Tag className="h-4 w-4 shrink-0" strokeWidth={2.5} />
      <span className="truncate"><span className="uppercase tracking-wide">{promoBadge(p)}</span> · {p.title}{p.description ? ` — ${p.description}` : ""}</span>
      <ArrowRight className="h-4 w-4 shrink-0" />
    </button>
  );
}

function OfferStrip() {
  const navigate = useNavigate();
  const [promos, setPromos] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/promotions/active"), api.get("/vehicles")])
      .then(([p, v]) => { setPromos((p.data || []).slice(0, 3)); setVehicles(v.data || []); })
      .catch(() => {});
  }, []);
  if (!promos.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 pt-14 sm:px-6" data-testid="offer-strip">
      <div className="flex items-end justify-between">
        <div>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-rose-600">Live offers</p>
          <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">This week's specials</h2>
        </div>
        <button onClick={() => navigate("/specials")} className="hidden items-center gap-1 text-sm font-bold text-rose-600 hover:underline sm:inline-flex" data-testid="offer-strip-all">All specials <ArrowRight className="h-4 w-4" /></button>
      </div>
      <div className="mt-6 grid gap-5 md:grid-cols-3">
        {promos.map((p) => {
          const count = vehicles.filter((v) => promoMatchesVehicle(p, v)).length;
          const fig = headlineFigure(p);
          return (
            <button key={p.id} data-testid={`offer-strip-card-${p.id}`} onClick={() => { api.post(`/promotions/${p.id}/click`).catch(() => {}); navigate("/specials"); }}
              className="group flex flex-col rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 text-left transition-all duration-200 hover:-translate-y-1 hover:border-red-400 hover:shadow-xl hover:shadow-red-600/10">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <BrandMark make={p.make} logoUrl={p.logo_url} />
                  <div className="min-w-0">
                    {scopeLabel(p) && <p className="truncate text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{scopeLabel(p)}</p>}
                    <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${PROMO_TYPES[p.promo_type]?.cls || "bg-rose-600 text-white"}`}><Tag className="h-3 w-3" /> {promoBadge(p)}</span>
                  </div>
                </div>
                <OfferCountdown promo={p} className="shrink-0" testId={`offer-expiry-${p.id}`} />
              </div>
              {fig ? (
                <>
                  <p data-testid={`offer-headline-${p.id}`} className="mt-4 font-display text-2xl sm:text-3xl font-black leading-none tracking-tight break-words text-red-600 dark:text-red-500">{fig}</p>
                  <h3 className="mt-1.5 font-display text-base font-bold text-slate-700 dark:text-slate-200">{p.title}</h3>
                </>
              ) : (
                <h3 data-testid={`offer-headline-${p.id}`} className="mt-4 font-display text-2xl sm:text-3xl font-black leading-none tracking-tight break-words text-red-600 dark:text-red-500">{p.title}</h3>
              )}
              {termLine(p) && <p className="mt-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400">{termLine(p)}</p>}
              {p.description && <p className="mt-2 flex-1 text-sm text-slate-600 dark:text-slate-300">{p.description}</p>}
              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs font-semibold text-slate-500 dark:text-slate-400">
                <span className="inline-flex items-center gap-1"><Car className="h-3.5 w-3.5" /> {count} in stock</span>
                <span className="inline-flex items-center gap-1 text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" /> Broker-verified</span>
              </div>
              <span className="mt-4 inline-flex items-center text-sm font-semibold text-rose-600">View offer <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const c = useContent("landing", LANDING_DEFAULTS);

  useEffect(() => {
    api.get("/vehicles", { params: { verified_only: true } }).then(({ data }) => setVehicles(data.slice(0, 6))).catch(() => {});
  }, []);

  const intents = [
    { id: "concierge", icon: Sparkles, badge: c.intent1_badge, title: c.intent1_title, desc: c.intent1_desc, cta: "Start concierge", path: "/concierge", testId: "landing-intent-concierge", primary: true },
    { id: "link", icon: Link2, badge: c.intent2_badge, title: c.intent2_title, desc: c.intent2_desc, cta: "Paste a link", path: "/request/new", testId: "landing-intent-link" },
    { id: "browse", icon: Search, badge: c.intent3_badge, title: c.intent3_title, desc: c.intent3_desc, cta: "Explore inventory", path: "/inventory", testId: "landing-intent-browse" },
  ];

  const values = [
    { icon: BadgeCheck, t: c.value1_title, d: c.value1_desc },
    { icon: ShieldCheck, t: c.value2_title, d: c.value2_desc },
    { icon: HandCoins, t: c.value3_title, d: c.value3_desc },
    { icon: Truck, t: c.value4_title, d: c.value4_desc },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <SpecialsBanner />

      <section className="relative overflow-hidden bg-slate-950 text-white">

        <div className="absolute inset-0">
          <img src={c.hero_image} alt="Stealth luxury performance car under studio lighting" className="h-full w-full object-cover opacity-40" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/30" />
        <div className="ab-hero-grid absolute inset-0 opacity-40" />
        <div className="ab-glow absolute inset-0" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:py-28">
          <div className="ab-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-300">
              <Home className="h-3.5 w-3.5" /> {c.hero_badge}
            </span>
            <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {c.hero_title}<br /><span className="text-rose-500">{c.hero_title_accent}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
              {c.hero_subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="landing-hero-dream-car-cta" onClick={() => navigate("/concierge")} className="rounded-md bg-rose-600 px-6 py-6 text-base font-bold uppercase tracking-wide text-white shadow-lg shadow-rose-600/25 transition-all hover:bg-rose-700 active:scale-[0.98]">
                <Sparkles className="mr-1.5 h-4 w-4" /> {c.hero_cta_primary}
              </Button>
              <Button data-testid="landing-hero-browse-stock-cta" variant="outline" onClick={() => navigate("/inventory")} className="rounded-md border-slate-600 bg-transparent px-6 py-6 text-base font-semibold text-white hover:bg-slate-800 hover:text-white">
                {c.hero_cta_secondary} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold text-slate-400 dark:text-slate-500">
              <span className="inline-flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-rose-400" /> Stock verified with the dealer</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-rose-400" /> No sales-floor pressure</span>
              <span className="inline-flex items-center gap-1.5"><Truck className="h-4 w-4 text-rose-400" /> Home delivery statewide</span>
            </div>
          </div>
        </div>
      </section>

      <OfferStrip />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-rose-600">{c.start_eyebrow}</p>
        <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl lg:text-4xl">{c.start_heading}</h2>
        <p className="mt-2 max-w-2xl text-slate-600 dark:text-slate-300">{c.start_subtext}</p>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {intents.map((c) => (
            <button key={c.id} data-testid={c.testId} onClick={() => navigate(c.path)}
              className={`group flex flex-col rounded-md border p-8 text-left transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${c.primary ? "border-rose-200 bg-gradient-to-b from-rose-50/60 to-white" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-400"}`}>
              <div className="flex items-center justify-between">
                <div className={`flex h-11 w-11 items-center justify-center rounded-md ${c.primary ? "bg-rose-600 text-white" : "bg-slate-950 text-white"}`}><c.icon className="h-5 w-5" strokeWidth={2.5} /></div>
                <span className={`rounded-full border px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${c.primary ? "border-rose-200 bg-white dark:bg-slate-900 text-rose-600" : "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400"}`}>{c.badge}</span>
              </div>
              <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{c.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{c.desc}</p>
              <span className="mt-5 inline-flex items-center text-sm font-semibold text-rose-600">{c.cta} <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
            </button>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 dark:border-slate-800 bg-slate-950 text-white">
        <div className="ab-grain absolute" />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-rose-500">{c.why_eyebrow}</p>
          <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">{c.why_heading}</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((s, i) => (
              <div key={i} className="rounded-md border border-slate-800 bg-slate-900/60 p-6 transition-colors hover:border-slate-700">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-rose-600/15 text-rose-400"><s.icon className="h-5 w-5" strokeWidth={2.5} /></div>
                <h3 className="mt-4 font-display text-base font-bold text-white">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400 dark:text-slate-500">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {vehicles.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="flex items-end justify-between">
            <div>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-rose-600">{c.picks_eyebrow}</p>
              <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl lg:text-4xl">{c.picks_heading}</h2>
            </div>
            <Button variant="ghost" onClick={() => navigate("/inventory")} data-testid="view-all-btn" className="text-rose-600 hover:text-rose-700">View all <ArrowRight className="ml-1 h-4 w-4" /></Button>
          </div>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {vehicles.map((v) => <VehicleCard key={v.id} v={v} />)}
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  );
}
